import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { themes } from '../styles/ColorStyles'

const nm = themes.neumorphic

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const HeroContainer = styled.section`
  min-height: 100vh;
  background: ${nm.bg};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 5vw;
  overflow: hidden;
  @media (max-width: 968px) {
    padding: 100px 5vw 60px;
    min-height: auto;
  }
`
const ContentWrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 1;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`
const TextSection = styled.div`
  animation: ${fadeInUp} 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s backwards;
  @media (max-width: 968px) { order: 2; }
`
const Eyebrow = styled.p`
  font-family: bree, sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: ${nm.accent};
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
`
const Title = styled.h1`
  font-family: bree, sans-serif;
  font-size: clamp(28px, 5vw, 72px);
  font-weight: 700;
  line-height: 1.05;
  white-space: nowrap;
  color: ${nm.text1};
  margin: 0 0 24px 0;
  letter-spacing: -0.03em;
`
const Description = styled.p`
  font-family: bree, sans-serif;
  font-size: clamp(18px, 2vw, 22px);
  line-height: 1.6;
  color: ${nm.text2};
  margin: 0 0 40px 0;
  max-width: 600px;
  font-weight: 400;
`
const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`
/* Primary text button — View Research */
const PrimaryButton = styled.a`
  font-family: bree, sans-serif;
  font-size: 17px;
  font-weight: 500;
  padding: 12px 26px;
  border-radius: 50px;
  text-decoration: none;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${nm.bg};
  color: ${nm.accent};
  box-shadow: ${nm.shadow};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  &:hover { box-shadow: ${nm.shadowHover}; color: ${nm.accent}; }
  &:active { box-shadow: ${nm.shadowInset}; }
`
/* Square icon buttons — social links */
const IconButton = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  text-decoration: none;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${nm.bg};
  color: ${nm.text2};
  box-shadow: ${nm.shadowSmall};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
  &:hover { box-shadow: ${nm.shadow}; color: ${nm.text1}; }
  &:active { box-shadow: ${nm.shadowInset}; }
`
/* Inline SVG icons */
const IconBook = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
)
const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)
const IconInstagram = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
)
const IconScholar = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/>
  </svg>
)
const CardSection = styled.div`
  animation: ${fadeIn} 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s backwards;
  @media (max-width: 968px) { order: 1; }
`
const CanvasContainer = styled.div`
  width: 100%;
  aspect-ratio: 1/1;
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: ${nm.shadowLarge};
  background: ${nm.bg};
  @media (max-width: 968px) {
    max-width: 400px;
    margin: 0 auto;
  }
`

var HeroSectionV3 = function() {
  var _s = useState(''); var iframeSrc = _s[0]; var setIframeSrc = _s[1]

  useEffect(function() {
    if (typeof window === 'undefined') return
    setIframeSrc('/portrait-3d.html')
  }, [])

  return (
    <HeroContainer>
      <ContentWrapper>
        <TextSection>
          <Eyebrow>Research Scientist @ Adobe Research</Eyebrow>
          <Title>Tianyu (Steve) Wang</Title>
          <ButtonGroup>
            <PrimaryButton href="#publications">
              <IconBook />
              View Research
            </PrimaryButton>
            <IconButton href="https://x.com/VisionSteve" target="_blank" rel="noopener noreferrer" title="X / Twitter">
              <IconX />
            </IconButton>
            <IconButton href="https://instagram.com/steve.wong.film" target="_blank" rel="noopener noreferrer" title="Instagram">
              <IconInstagram />
            </IconButton>
            <IconButton href="https://scholar.google.com/citations?user=yRwZIN8AAAAJ" target="_blank" rel="noopener noreferrer" title="Google Scholar">
              <IconScholar />
            </IconButton>
          </ButtonGroup>
        </TextSection>
        <CardSection>
          <CanvasContainer>
            {iframeSrc && (
              <iframe
                src={iframeSrc}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '24px',
                }}
                allow="gyroscope; accelerometer"
                title="3D Portrait"
              />
            )}
          </CanvasContainer>
        </CardSection>
      </ContentWrapper>
    </HeroContainer>
  )
}

export default HeroSectionV3
